import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Loader } from 'lucide-react';

/**
 * SearchInput - Premium debounced search component
 *
 * Props:
 *  - value: string (controlled)
 *  - onChange: (val: string) => void
 *  - onSearch: (val: string) => void  (called after debounce)
 *  - placeholder: string
 *  - debounceMs: number (default 350)
 *  - isLoading: boolean (show spinner while searching)
 *  - disabled: boolean
 *  - className: string
 *  - id: string
 */
export default function SearchInput({
  value = '',
  onChange,
  onSearch,
  placeholder = 'Search...',
  debounceMs = 350,
  isLoading = false,
  disabled = false,
  className = '',
  id = 'search-input',
}) {
  const [isFocused, setIsFocused] = useState(false);
  const timerRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    if (onChange) onChange(val);

    if (onSearch) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onSearch(val);
      }, debounceMs);
    }
  };

  const handleClear = () => {
    if (onChange) onChange('');
    if (onSearch) onSearch('');
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div className={`ui-search-wrapper ${isFocused ? 'focused' : ''} ${disabled ? 'disabled' : ''} ${className}`} id={id}>
      <span className="ui-search-icon">
        {isLoading ? (
          <Loader size={17} className="ui-search-spinner" />
        ) : (
          <Search size={17} />
        )}
      </span>
      <input
        type="text"
        className="ui-search-input"
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={placeholder}
        autoComplete="off"
        spellCheck="false"
      />
      {value && !disabled && (
        <button
          type="button"
          className="ui-search-clear-btn"
          onClick={handleClear}
          aria-label="Clear search"
          tabIndex={-1}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
